import { useState, useEffect, type ButtonHTMLAttributes } from "react";
import { Loader2, Check } from "lucide-react";

type Variant = "primary" | "secondary" | "danger" | "ghost";

interface LoadingButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  loadingText?: string;
  /** Show a success checkmark animation for 1.5s when this changes to true */
  showSuccess?: boolean;
  variant?: Variant;
  size?: "sm" | "md" | "lg";
}

const variantStyles: Record<Variant, string> = {
  primary:
    "bg-teal-600 text-white hover:bg-teal-700 focus:ring-teal-500 disabled:bg-teal-300",
  secondary:
    "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-400 disabled:bg-gray-100",
  danger:
    "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-red-300",
  ghost:
    "bg-transparent text-gray-600 hover:bg-gray-100 focus:ring-gray-300 disabled:text-gray-300",
};

const sizeStyles: Record<string, string> = {
  sm: "px-3 py-1.5 text-xs gap-1.5",
  md: "px-4 py-2 text-sm gap-2",
  lg: "px-6 py-3 text-base gap-2.5",
};

export function LoadingButton({
  isLoading = false,
  loadingText,
  showSuccess = false,
  variant = "primary",
  size = "md",
  children,
  disabled,
  className = "",
  ...props
}: LoadingButtonProps) {
  const [showCheck, setShowCheck] = useState(false);

  // Show success checkmark briefly
  useEffect(() => {
    if (showSuccess) {
      setShowCheck(true);
      const timer = setTimeout(() => setShowCheck(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const baseClasses =
    "inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed";

  return (
    <button
      disabled={disabled || isLoading}
      className={`${baseClasses} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2
            className={`animate-spin ${size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"}`}
          />
          <span>{loadingText || "Loading..."}</span>
        </>
      ) : showCheck ? (
        <>
          <Check
            className={`text-emerald-400 ${size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"}`}
          />
          <span>Done!</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}
